import { Table } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useJobScheduleEventsQuery } from './jobScheduleQueries'
import { JobScheduleEvent } from './jobScheduleApi'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSnowflake } from '@fortawesome/pro-solid-svg-icons'
import { useUsersQuery } from '../../Users/usersQueries'

dayjs.extend(relativeTime)

type Props = {
  jobId: number
}

const JobScheduleTable = ({ jobId }: Props) => {
  const { data: events, isLoading } = useJobScheduleEventsQuery(jobId)
  const { data: users } = useUsersQuery(false)

  const getUser = (email: string | null) => {
    if (!email || !users) {
      return undefined
    }
    return users.find(u => u.email === email)
  }

  const getWorkingDays = (event: JobScheduleEvent) => {
    const start = dayjs(event.startUtc)
    const end = dayjs(event.endUtc)
    return Math.max(1, end.diff(start, 'day'))
  }

  return (
    <section>
      <Table
        dataSource={events ?? []}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={false}
        columns={[
          {
            title: 'Scheduled On',
            key: 'scheduledOn',
            render: (_, event: JobScheduleEvent) => {
              const start = dayjs(event.startUtc)
              const workingDays = getWorkingDays(event)
              const dateStr = start.format('MMM D, YYYY')

              if (workingDays > 1) {
                const end = dayjs(event.endUtc)
                return <span>{dateStr} - {end.format('MMM D, YYYY')}</span>
              }
              return <span>{dateStr}</span>
            },
            sorter: (a: JobScheduleEvent, b: JobScheduleEvent) => dayjs(a.startUtc).unix() - dayjs(b.startUtc).unix(),
            defaultSortOrder: 'ascend'
          },
          {
            title: 'In',
            key: 'in',
            render: (_, event: JobScheduleEvent) => dayjs(event.startUtc).fromNow()
          },
          {
            title: 'Work Areas',
            key: 'workAreaNames',
            render: (_, event: JobScheduleEvent) =>
              (event.workAreaNames ?? []).join(', ')
          },
          {
            title: 'Assigned To',
            key: 'crewForemanEmail',
            render: (_, event: JobScheduleEvent) => {
              const user = getUser(event.crewForemanEmail)
              return (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {user?.avatar && (
                    <img
                      src={user.avatar}
                      alt=""
                      style={{ width: '2.5em', height: '2.5em', borderRadius: '50%', marginRight: '0.5em', opacity: 0.8 }}
                    />
                  )}
                  {event.crewForemanEmail}
                </span>
              )
            }
          },
          {
            title: 'Manhours/day',
            dataIndex: 'dailyManhours',
            key: 'dailyManhours',
            align: 'center',
            render: (value: number | null) => value ?? ''
          },
          {
            title: 'Working Days',
            key: 'workingDays',
            align: 'center',
            render: (_, event: JobScheduleEvent) => {
              const days = getWorkingDays(event)
              return `${days} day${days > 1 ? 's' : ''}`
            }
          },
          {
            title: 'Frozen',
            dataIndex: 'frozen',
            key: 'frozen',
            align: 'center',
            render: (frozen: boolean) => frozen ? <FontAwesomeIcon icon={faSnowflake} /> : null
          },
          {
            title: 'Scheduling Notes',
            dataIndex: 'schedulingNotes',
            key: 'schedulingNotes',
            ellipsis: true
          }
        ]}
      />
    </section>
  )
}

export default JobScheduleTable
